import { useEffect, useState } from "react";
import type { TimelineDay } from "../../utils/timeline";

export interface CurrentEventState {
  /** Id of the event happening now, or null. */
  currentId: string | null;
  /** Id of the next upcoming event, or null. */
  nextId: string | null;
}

const NONE: CurrentEventState = { currentId: null, nextId: null };

/**
 * Returns the venue-local calendar date ("YYYY-MM-DD") and wall-clock time
 * ("HH:mm") for `now` in `timeZone`. Falls back to the device clock when no
 * zone is provided.
 */
function nowInZone(timeZone: string | undefined): { date: string; time: string } {
  const now = new Date();
  if (!timeZone) {
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return { date: `${y}-${mo}-${d}`, time: `${hh}:${mm}` };
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  let hour = get("hour");
  if (hour === "24") hour = "00"; // some engines emit 24 for midnight
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hour}:${get("minute")}`,
  };
}

/**
 * Tracks which event is happening now / up next across all days, but only on
 * a wedding day itself (when the venue-local date matches one of the days).
 * Recomputes every 30 seconds. Returns event ids so the renderer can flag the
 * right rows. `days` should already be sorted (events by time, days by date).
 */
export function useCurrentEvent(
  days: TimelineDay[],
  timeZone: string | undefined,
): CurrentEventState {
  const [state, setState] = useState<CurrentEventState>(NONE);

  useEffect(() => {
    const items = days.flatMap((day) =>
      day.events.map((event) => ({
        id: event.id,
        key: `${day.date}T${event.time}`,
      })),
    );
    if (items.length === 0) {
      setState(NONE);
      return;
    }
    items.sort((a, b) => a.key.localeCompare(b.key));
    const dayDates = new Set(days.map((d) => d.date).filter(Boolean));

    const compute = () => {
      const { date, time } = nowInZone(timeZone);
      if (!dayDates.has(date)) {
        setState(NONE);
        return;
      }
      const nowKey = `${date}T${time}`;
      let currentId: string | null = null;
      let nextId: string | null = null;
      for (const item of items) {
        if (item.key <= nowKey) currentId = item.id;
        else {
          nextId = item.id;
          break;
        }
      }
      setState({ currentId, nextId });
    };
    compute();
    const id = setInterval(compute, 30_000);
    return () => clearInterval(id);
  }, [days, timeZone]);

  return state;
}
